import { Injectable, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  ContextService,
  SystemService,
} from '@maximizer/core/shared/data-access';
import {
  IS_BROWSER_ONLY_ROUTE,
  OAuth2Service,
  STORAGE_KEY_PARTITION,
} from '@maximizer/oauth2/data-access';
import {
  catchError,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
  of,
  tap,
  timeout,
} from 'rxjs';
import { InsightsService } from '@maximizer/core/shared/insights';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import {
  IdentityService,
  InitializationService,
  OutlookInsightsEvents,
  OutlookSessionService,
  OutlookStore,
  TokenMonitorService,
  getDecodedAccessToken,
  requestHandshakeFromOpener,
} from '@maximizer/outlook/shared/data-access';
import {
  GlobalServicesLoginDetails,
  Session,
} from '@maximizer/core/shared/domain';
import {
  deobfuscateStorageValue,
  normalizeError,
  obfuscateStorageValue,
} from '@maximizer/shared/util';

@Injectable({
  providedIn: 'root',
})
export class OutlookAuthService {
  private readonly TOKEN_REFRESH_TIMEOUT = 15000;
  private readonly CIRCUIT_BREAKER_RECOVERY_DELAY = 1000;
  private readonly CLEAN_STATE = '';
  private initializationComplete = false;
  private initializationPromise?: Promise<void>;

  private readonly contextService = inject(ContextService);
  private readonly oauth2Service = inject(OAuth2Service);
  private readonly insightsService = inject(InsightsService);
  private readonly session = inject(OutlookSessionService);
  private readonly outlookStore = inject(OutlookStore);
  private readonly systemService = inject(SystemService);
  private readonly identityService = inject(IdentityService);
  private readonly router = inject(Router);
  private readonly initializationService = inject(InitializationService);
  private readonly tokenMonitorService = inject(TokenMonitorService);
  private readonly isBrowserOnlyRoute = inject(IS_BROWSER_ONLY_ROUTE);

  constructor() {
    effect(() => {
      const reason = this.tokenMonitorService.terminalFailure();
      if (reason) {
        this.tokenMonitorService.clearTerminalFailure();
        void this.handleAuthFailure(reason);
      }
    });
  }

  async initialize(): Promise<void> {
    if (this.initializationPromise !== undefined) {
      return this.initializationPromise;
    }

    if (this.initializationComplete) {
      return;
    }

    this.initializationPromise = this.initializeInternal();
    return this.initializationPromise;
  }

  private async initializeInternal(): Promise<void> {
    try {
      await this.hydrateFromSyncConfigOpenerIfNeeded();

      this.setOAuth2ServiceKeys(this.officeEmail);

      const authState = await this.checkInitialAuthState();
      const storagePopulated =
        await this.handleStorageRecoveryIfNeeded(authState);

      if (
        this.hasValidCredentials(
          authState.propertiesPopulated,
          storagePopulated,
          authState.tokenExpired,
        )
      ) {
        this.trackTrace('auth-service-valid-credentials-found');
        await this.finalizeAuthorizationState();
      } else {
        this.trackTrace('auth-service-refresh-required');
        await this.refreshTokenOrHandleError();
      }
      if (this.outlookStore.isAuthorized()) {
        this.initializationComplete = true;
      }
    } catch (error) {
      this.initializationComplete = false;
      this.initializationPromise = undefined;
      await this.handleInitializationError(error);
    } finally {
      this.initializationPromise = undefined;
    }
  }

  private async hydrateFromSyncConfigOpenerIfNeeded(): Promise<void> {
    if (!this.isBrowserOnlyRoute) return;
    if (!window.opener) return;

    if (this.oauth2Service.requiredPropertiesPopulated) return;

    try {
      const hydrated = await requestHandshakeFromOpener(this.oauth2Service);
      this.trackTrace('auth-service-sync-config-handshake', undefined, {
        hydrated,
      });
    } catch (error) {
      this.trackException('auth-service-sync-config-handshake-failed', error);
    }
  }

  private get cachedOfficeEmail(): string | undefined {
    const stored = localStorage.getItem(STORAGE_KEY_PARTITION);
    return stored ? deobfuscateStorageValue(stored) : undefined;
  }

  private set cachedOfficeEmail(value: string | undefined) {
    if (value) {
      localStorage.setItem(STORAGE_KEY_PARTITION, obfuscateStorageValue(value));
    }
  }

  private get officeEmail(): string | undefined {
    try {
      const liveEmail =
        Office?.context?.mailbox?.userProfile?.emailAddress?.toLocaleLowerCase();

      if (liveEmail) {
        this.cachedOfficeEmail = liveEmail;
        return liveEmail;
      }

      if (this.cachedOfficeEmail) {
        this.trackTrace(
          'auth-service-office-context-unavailable-using-cache',
          undefined,
          {
            hasLiveContext: false,
          },
        );
        return this.cachedOfficeEmail;
      }

      return undefined;
    } catch (error) {
      if (this.cachedOfficeEmail) {
        this.trackTrace(
          'auth-service-office-context-error-using-cache',
          undefined,
          {
            hasLiveContext: false,
          },
        );
        return this.cachedOfficeEmail;
      }

      const errorResult =
        error instanceof Error
          ? error
          : new Error('Office context access error');

      this.trackException(
        'auth-service-office-context-access-error',
        errorResult,
      );
      return undefined;
    }
  }

  private get isTokenExpired(): boolean {
    return !this.oauth2Service.isAccessTokenEffectivelyValid();
  }

  private async checkInitialAuthState() {
    const propertiesPopulated = this.oauth2Service.requiredPropertiesPopulated;
    const storagePopulated = await this.oauth2Service.populateFromStorage();

    const tokenExpired = this.isTokenExpired;

    this.trackTrace(
      'auth-service-init-start',
      undefined,
      {
        propertiesPopulated,
        storagePopulated,
        tokenExpired,
        expiresAt: this.oauth2Service.oAuth2Token?.expires_at ?? null,
        isCloud: this.outlookStore.isCloud(),
        partitionKeyPresent: !!this.oauth2Service.storagePartitionKey,
        hasLiveOfficePartitionKey: this.hasLiveOfficePartitionKey(),
        hasPersistedPartitionKey:
          !!this.oauth2Service.getPersistedPartitionKey(),
        hasCachedOfficeEmail: !!this.cachedOfficeEmail,
      },
      SeverityLevel.Information,
    );

    return { propertiesPopulated, storagePopulated, tokenExpired };
  }

  private async handleStorageRecoveryIfNeeded(authState: {
    propertiesPopulated: boolean;
    storagePopulated: boolean;
  }): Promise<boolean> {
    if (!authState.storagePopulated && !authState.propertiesPopulated) {
      this.trackTrace(
        'auth-service-storage-recovery-attempt',
        'storage-population-failed',
        {
          propertiesPopulated: authState.propertiesPopulated,
          storagePopulated: authState.storagePopulated,
        },
      );
      await this.recoverFromStorageCorruption();
    }

    return authState.storagePopulated;
  }

  private hasValidCredentials(
    propertiesPopulated: boolean,
    storagePopulated: boolean,
    tokenExpired: boolean,
  ): boolean {
    const expiresAt = this.oauth2Service.oAuth2Token?.expires_at;

    if (tokenExpired) {
      this.trackTrace('auth-service-credentials-invalid', 'token-expired', {
        tokenExpiresAt:
          expiresAt instanceof Date
            ? expiresAt.toISOString()
            : (expiresAt ?? null),
      });
      return false;
    }

    if (!storagePopulated && !propertiesPopulated) {
      this.trackTrace('auth-service-credentials-invalid', 'missing-storage', {
        storagePopulated,
        propertiesPopulated,
      });
      return false;
    }

    return propertiesPopulated || storagePopulated;
  }

  private async finalizeAuthorizationState(): Promise<void> {
    const contextPopulated = await this.populateContext();
    if (contextPopulated) {
      this.outlookStore.setIsAuthorized(true);
      this.trackTrace(
        'auth-service-flow-completed-success',
        undefined,
        undefined,
        SeverityLevel.Information,
      );
    } else {
      this.trackException(
        'auth-service-context-population-failed',
        new Error('Context population failed during auth initialization'),
      );

      const existingError = sessionStorage.getItem('authError');
      await this.handleAuthFailure(existingError ?? 'session-failed');
    }
  }

  private async handleInitializationError(error: unknown): Promise<void> {
    this.trackException('auth-service-initialize-error', error);
    const recovered = await this.attemptErrorRecovery(error);
    if (recovered) {
      await this.initialize();
      return;
    }

    await this.handleAuthFailure('auth-initialization-failed');
  }

  private async attemptErrorRecovery(error: unknown): Promise<boolean> {
    try {
      if (this.isStorageError(error)) {
        return await this.recoverFromStorageCorruption();
      } else if (this.isCircuitBreakerError(error)) {
        return await this.recoverFromCircuitBreakerError();
      }
      return false;
    } catch (recoveryError) {
      this.trackException('auth-service-recovery-failed', recoveryError);
      return false;
    }
  }

  private setOAuth2ServiceKeys(officeEmail: string | undefined): void {
    const livePartitionKey = this.getOfficePartitionKey();

    const hasExistingRealKeys =
      this.cachedOfficeEmail && this.oauth2Service.storagePartitionKey;

    if (!officeEmail && hasExistingRealKeys) {
      this.trackTrace('auth-service-keys-preserved-context-stale', undefined, {
        existingPartitionKeyPresent: !!this.oauth2Service.storagePartitionKey,
      });
      return;
    }

    const partitionKey = livePartitionKey ?? officeEmail;
    if (!partitionKey) {
      return;
    }

    this.oauth2Service.storagePartitionKey = partitionKey;
    if (officeEmail) {
      this.oauth2Service.encryptPassword = officeEmail;
    }
    this.oauth2Service.persistPartitionKey();
  }

  private hasLiveOfficePartitionKey(): boolean {
    try {
      return !!Office?.context?.partitionKey;
    } catch {
      return false;
    }
  }

  private getOfficePartitionKey(): string | undefined {
    try {
      const liveKey = Office?.context?.partitionKey;
      if (liveKey) {
        return liveKey;
      }
      return this.oauth2Service.getPersistedPartitionKey();
    } catch {
      return this.oauth2Service.getPersistedPartitionKey();
    }
  }

  private async refreshTokenOrHandleError(): Promise<void> {
    const callerTag = 'outlook-auth-service-init';
    const hadStoredCredentials = await this.oauth2Service.getStorageToken({
      skipRefresh: true,
    });
    const hadStoredRefreshToken = !!hadStoredCredentials?.refresh_token;

    this.trackTrace('auth-service-token-refresh-start', undefined, {
      hasStoredCredentials: !!hadStoredCredentials,
      hasStoredRefreshToken: hadStoredRefreshToken,
      refreshTimeoutMs: this.TOKEN_REFRESH_TIMEOUT,
      callerTag,
    });

    if (!hadStoredCredentials || !hadStoredRefreshToken) {
      const reason = hadStoredCredentials
        ? 'no-refresh-token'
        : 'no-stored-credentials';
      this.trackTrace(
        'auth-service-token-refresh-skipped',
        reason,
        {
          hasStoredCredentials: !!hadStoredCredentials,
          hasStoredRefreshToken: hadStoredRefreshToken,
        },
        SeverityLevel.Warning,
      );

      await this.handleAuthFailure(this.CLEAN_STATE, false, true);
      return;
    }

    const result = await firstValueFrom(
      this.oauth2Service.refreshToken(undefined, true, callerTag).pipe(
        timeout(this.TOKEN_REFRESH_TIMEOUT),
        map((token) => token ?? undefined),
        catchError((error) => {
          this.contextService.token = '';
          this.trackException('auth-service-token-refresh-error', error);
          return of(undefined);
        }),
      ),
    );

    if (result) {
      this.trackTrace('auth-service-token-refresh-success', undefined, {
        refreshTokenUsed: hadStoredRefreshToken,
        tokenType: result.token_type,
        receivedExpiresAt: result.expires_at,
      });
      this.session.clearSessionCache();
      this.contextService.token = '';

      const contextPopulated = await this.populateContext();
      if (contextPopulated) {
        this.outlookStore.setIsAuthorized(true);
        this.trackTrace(
          'auth-service-token-refresh-completed',
          undefined,
          {
            contextPopulated,
            isCloud: this.outlookStore.isCloud(),
          },
          SeverityLevel.Information,
        );
      } else {
        this.trackException(
          'auth-service-token-refresh-context-failed',
          new Error('Context population failed after successful token refresh'),
        );

        const existingError = sessionStorage.getItem('authError');
        await this.handleAuthFailure(existingError ?? 'session-failed');
      }
    } else {
      const refreshFailureReason =
        this.oauth2Service.getLastRefreshFailureReason();
      const isLocalRefreshFailure =
        refreshFailureReason === 'NETWORK_ERROR' ||
        refreshFailureReason === 'NETWORK_ERROR_TOKEN_EXPIRED';

      if (isLocalRefreshFailure) {
        const fallbackAccessToken =
          this.oauth2Service.oAuth2Token?.access_token;
        this.contextService.token = fallbackAccessToken ?? '';
        this.outlookStore.setIsAuthorized(!!fallbackAccessToken);

        this.trackTrace(
          'auth-service-token-refresh-local-network-error-ignored',
          refreshFailureReason,
          {
            hasFallbackAccessToken: !!fallbackAccessToken,
            authorizedState: this.outlookStore.isAuthorized(),
          },
        );
        return;
      }

      this.trackException(
        'auth-service-token-refresh-failed',
        new Error('Token refresh failed - user session expired'),
      );

      await this.handleAuthFailure('session-failed');
    }
  }

  private async populateContext(): Promise<boolean> {
    const {
      requiredPropertiesPopulated,
      mxUserEmail,
      oAuth2Token,
      cloudLoginDetails,
      onPremLoginDetails,
    } = this.oauth2Service;

    if (!requiredPropertiesPopulated || !mxUserEmail) {
      this.trackException(
        'auth-service-context-missing-required-properties',
        new Error('Missing required properties or email'),
        {
          requiredPropertiesPopulated,
          hasEmail: !!mxUserEmail,
          hasOAuthToken: !!oAuth2Token,
          isCloud: this.outlookStore.isCloud(),
        },
      );
      return false;
    }

    const isCloud =
      this.outlookStore.isCloud() ?? this.readIsCloudStorageAndSetStore();
    this.contextService.token = oAuth2Token?.access_token ?? '';

    if (isCloud) {
      const loginDetailsList = await this.fetchLoginDetailsOrClear(mxUserEmail);
      if (!loginDetailsList) {
        this.trackException(
          'auth-service-context-cloud-login-details-missing',
          new Error('Failed to fetch login details or required properties'),
          { reason: 'login-details-list-missing' },
        );
        sessionStorage.setItem('authError', 'loginDetails');
        return false;
      }

      if (!this.oauth2Service.requiredPropertiesPopulated) {
        this.trackException(
          'auth-service-context-cloud-login-details-missing',
          new Error('Missing login details for cloud mode'),
          {
            loginDetailsCount: loginDetailsList.length,
            mxUserEmail,
            hasCloudLoginDetails: !!cloudLoginDetails,
            reason: 'login-details-properties-missing',
          },
        );
        sessionStorage.setItem('authError', 'loginDetails');
        return false;
      }

      const matchedLogin = loginDetailsList.find(
        (o) => o.tenant.id === cloudLoginDetails?.tenant.id,
      );

      if (!matchedLogin) {
        this.trackException(
          'auth-service-context-tenant-match-failed',
          new Error('No matching tenant found'),
          {
            tenantId: cloudLoginDetails?.tenant.id ?? 'unknown',
            loginDetailsCount: loginDetailsList.length,
            loginDetailTenantIds: loginDetailsList.map((o) => o.tenant.id),
            mxUserEmail,
            reason: 'tenant-not-found',
          },
        );
        await this.clearAuthAndSessionCacheWithError('loginDetails');
        await this.handleAuthFailure('loginDetails', false);
        return false;
      }

      this.oauth2Service.populateContextOAuthData(true);
      this.oauth2Service.setStorageLoginDetails(matchedLogin);

      this.trackTrace('auth-service-context-cloud-setup-success');
    } else {
      if (!onPremLoginDetails) {
        this.trackException(
          'auth-service-context-onprem-details-missing',
          new Error('Missing on-premise login details'),
          {
            mxUserEmail,
            hasOnPremLoginDetails: false,
            isCloud,
            reason: 'onprem-login-details-missing',
          },
        );
        this.initializationService.stopInitialization();
        return false;
      }

      this.oauth2Service.populateContextOAuthData(false);
      this.oauth2Service.setStorageOnPremLoginDetails(onPremLoginDetails);

      this.trackTrace('auth-service-context-onprem-setup-success');
    }

    const session = await lastValueFrom(this.getSessionAndStartAppInsights());

    if (!session) {
      if (this.session.isLastErrorLocalNetwork() && this.contextService.token) {
        this.trackTrace(
          'auth-service-context-session-network-error-ignored',
          'local-network',
          {
            mxUserEmail,
            tokenPresent: !!this.contextService.token,
            authorizedState: this.outlookStore.isAuthorized(),
          },
        );
        return true;
      }

      this.trackException(
        'auth-service-context-session-missing',
        new Error(
          'Get session info returned malformed data - cannot initialize app without session',
        ),
        {
          mxUserEmail,
          tokenPresent: !!this.contextService.token,
          authorizedState: this.outlookStore.isAuthorized(),
        },
      );

      await this.handleAuthFailure('session-failed');
      return false;
    }
    this.checkSystemInfoForSyncSupport();
    return true;
  }

  private async fetchLoginDetailsOrClear(
    email: string,
  ): Promise<GlobalServicesLoginDetails[] | null> {
    try {
      const result = await lastValueFrom(
        this.oauth2Service.getLoginDetails(email),
      );

      if (!result) {
        this.trackException(
          'auth-service-fetch-login-details-empty-result',
          new Error('Global services returned no login details'),
        );
        throw new Error('Login details not found');
      }
      return result;
    } catch (error) {
      this.trackException('auth-service-fetch-login-details-error', error, {
        decodedToken: getDecodedAccessToken(this.contextService.token),
      });

      await this.clearAuthAndSessionCacheWithError('loginDetails');
      return null;
    }
  }

  private getSessionAndStartAppInsights(): Observable<Session | undefined> {
    return this.session.getInfo().pipe(
      tap((result) => {
        if (!result) {
          this.trackException(
            'auth-service-session-info-no-result',
            new Error(
              'No session data returned from getInfo - malformed response or empty',
            ),
          );
          return;
        }

        this.oauth2Service.mxUserId = result.user.id;
        this.identityService.populateMaximizerIdentity();
        this.initializeInsightsService();
      }),
    );
  }

  private initializeInsightsService(): void {
    const outlookVersion = Office?.context?.platform?.toString() ?? 'unknown';
    this.insightsService.setContextData({ 'outlook-version': outlookVersion });

    this.insightsService.trackPageView({
      name: 'outlook-version',
      pageType: outlookVersion,
    });
  }

  private checkSystemInfoForSyncSupport(): void {
    this.systemService
      .getInfo()
      .pipe(
        map((systemInfo) =>
          systemInfo
            ? this.systemService.validateDbVersionForNewSync(systemInfo)
            : false,
        ),
        catchError((error) => {
          this.trackException('auth-service-system-info-check-failed', error, {
            decodedToken: getDecodedAccessToken(this.contextService.token),
          });
          return of(false);
        }),
      )
      .subscribe((valid) => {
        this.outlookStore.updateVersion(valid);
      });
  }

  private readIsCloudStorageAndSetStore(): boolean {
    const storedIsCloudValue =
      this.oauth2Service.getIsCloudFromStorageWithFallback();
    const resolvedIsCloud = storedIsCloudValue ?? true;

    this.outlookStore.setIsCloud(resolvedIsCloud);

    if (!resolvedIsCloud) {
      this.insightsService.disable();
    }

    return resolvedIsCloud;
  }

  private async clearAuthAndSessionCacheWithError(
    errorType: string,
  ): Promise<void> {
    await this.oauth2Service.clearAuth('auth-service-cache-clear', {
      trigger: 'clearAuthAndSessionCacheWithError',
      errorType: errorType ?? '',
    });
    this.session.clearSessionCache();
    if (errorType) {
      sessionStorage.setItem('authError', errorType);
      this.trackTrace(
        'auth-service-error-flag-set',
        'cache-clear',
        { errorType },
        SeverityLevel.Warning,
      );
    }
  }

  private async handleAuthFailure(
    reason: string,
    navigateToLogin = true,
    skipErrorMessage = false,
  ): Promise<void> {
    try {
      await this.oauth2Service.clearAuth('auth-service-handle-auth-failure', {
        trigger: 'handleAuthFailure',
        reason,
        navigateToLogin,
        skipErrorMessage,
      });
      this.clearInMemoryAuthState();
      this.initializationService.stopInitialization();

      if (reason && !skipErrorMessage) {
        sessionStorage.setItem('authError', reason);
        this.trackTrace(
          'auth-service-error-flag-set',
          'force-logout',
          { errorType: reason },
          SeverityLevel.Warning,
        );
      }

      if (navigateToLogin && !window.location.pathname.includes('/login')) {
        await this.router.navigate(['/login'], {
          replaceUrl: true,
        });
      }
    } catch (error) {
      const errorResult = normalizeError(error);
      this.trackException('auth-service-failure-handler-error', errorResult);
      this.initializationService.stopInitialization();
      window.location.href = '/login';
    }
  }

  private clearInMemoryAuthState(): void {
    this.outlookStore.setIsAuthorized(false);
    this.contextService.token = '';
    this.initializationComplete = false;
    this.session.clearSessionCache();
    this.trackTrace('auth-service-in-memory-auth-state-cleared');
  }

  private isStorageError(error: unknown) {
    const errorMessage = normalizeError(error).toLowerCase();
    return (
      errorMessage.includes('corrupted') ||
      errorMessage.includes('malformed') ||
      errorMessage.includes('decryption') ||
      (error as Error)?.name === 'CorruptedDataError'
    );
  }

  private isCircuitBreakerError(error: unknown) {
    const errorMessage = normalizeError(error).toLowerCase();
    return (
      errorMessage.includes('circuit breaker') ||
      errorMessage.includes('backoff active')
    );
  }

  private async recoverFromStorageCorruption(): Promise<boolean> {
    try {
      this.trackTrace('auth-service-storage-corruption-recovery-start');

      await this.oauth2Service.clearAuth(
        'auth-service-storage-corruption-recovery',
        {
          trigger: 'recoverFromStorageCorruption',
        },
      );
      this.session.clearSessionCache();

      this.trackTrace('auth-service-storage-corruption-recovery-success');
      return true;
    } catch (error) {
      this.trackException(
        'auth-service-storage-corruption-recovery-failed',
        error,
      );
      return false;
    }
  }

  private async recoverFromCircuitBreakerError(): Promise<boolean> {
    try {
      this.trackTrace('auth-service-circuit-breaker-recovery-start');

      await new Promise((resolve) =>
        setTimeout(resolve, this.CIRCUIT_BREAKER_RECOVERY_DELAY),
      );

      const testCredentials = await this.oauth2Service.getStorageToken({
        skipRefresh: true,
      });
      const recovered = !!testCredentials;

      if (recovered) {
        this.trackTrace('auth-service-circuit-breaker-recovery-success');
      } else {
        this.trackTrace(
          'auth-service-circuit-breaker-recovery-failed',
          'no-credentials-after-reset',
        );
      }

      return recovered;
    } catch (error) {
      this.trackException(
        'auth-service-circuit-breaker-recovery-failed',
        error,
      );
      return false;
    }
  }

  private trackTrace(
    message: string,
    reason?: string,
    metadata?: Record<string, unknown>,
    severityLevel: SeverityLevel = SeverityLevel.Verbose,
  ): void {
    this.insightsService.trackTrace(
      { message, severityLevel },
      {
        component: 'OutlookAuthService',
        eventId: OutlookInsightsEvents.OutlookAuth,
        ...this.withAuthState({ reason, ...metadata }),
      },
    );
  }

  private trackException(
    event: string,
    error: unknown,
    metadata?: Record<string, unknown>,
  ): void {
    const errorMessage = normalizeError(error);

    this.insightsService.trackException(
      { error: error instanceof Error ? error : new Error(errorMessage) },
      {
        component: 'OutlookAuthService',
        eventId: OutlookInsightsEvents.OutlookAuth,
        event,
        errorMessage,
        ...this.withAuthState(metadata),
      },
    );
  }

  private withAuthState(
    metadata?: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      isTokenExpired: this.isTokenExpired,
      hasOAuthToken: !!this.oauth2Service.oAuth2Token,
      hasEmail: !!this.oauth2Service.mxUserEmail,
      isAuthorized: this.outlookStore.isAuthorized(),
      ...metadata,
    };
  }
}
